import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { Building } from '@shared/models/design/building-design.model';
import { getBuildingDisplayName } from '@shared/helpers/design/building.helpers';
import { getGfaFromStoreys } from '@shared/helpers/design/shape.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { PANEL_CONTENT_DEFAULT_SPACING } from '@ui/layout/panels/panel.constants';
import { Icon } from '@ui/Icon';
import { Tooltip } from '@ui/Tooltip';
import { useBuildingDesign } from '../hooks/design.hooks';
import { useSelectBuilding } from '../hooks/storeys.hooks';
import { getStoreysHeight } from '@shared/helpers/design/storey.helpers';
import { getBuildingOpenStoreyId } from '../helpers/storey-selection.helpers';
import {
  useHasVisibleBuildingSelection,
  useIsBuildingOutlineSelected,
  useHoveredBuildingId,
  useRequestPropertiesPanel,
  useSelectBuildingOutline,
  useSelectedBuildingId,
  useSetHoveredBuildingId,
  useSetSelectedStoreyId,
} from '../store/design-store.hooks';

type BuildingListRow = {
  building: Building;
  /** Building name, e.g. "Building A". */
  label: string;
  gfa: number;
  height: number;
};

/**
 * Building overview shown while no building is selected (DEV-2576): one bar
 * per building.
 * Selecting a row switches back to the storey list and selects the whole building.
 */
export const BuildingList: React.FC = () => {
  const { classes } = useStyles();
  const design = useBuildingDesign();
  const selectBuilding = useSelectBuilding();
  const selectBuildingOutline = useSelectBuildingOutline();
  const requestPropertiesPanel = useRequestPropertiesPanel();
  const selectedBuildingId = useSelectedBuildingId();
  const hasVisibleBuildingSelection = useHasVisibleBuildingSelection();
  const isBuildingOutlineSelected = useIsBuildingOutlineSelected();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const hoveredBuildingId = useHoveredBuildingId();
  const setHoveredBuildingId = useSetHoveredBuildingId();

  const handleSelectBuilding = useCallback(
    (buildingId: Building['id']) => {
      selectBuilding(buildingId);
      selectBuildingOutline(buildingId);
      requestPropertiesPanel();
    },
    [requestPropertiesPanel, selectBuilding, selectBuildingOutline],
  );

  const handleOpenBuilding = useCallback(
    (building: Building) => {
      selectBuilding(building.id);
      setSelectedStoreyId(getBuildingOpenStoreyId(building.storeys));
      requestPropertiesPanel();
    },
    [requestPropertiesPanel, selectBuilding, setSelectedStoreyId],
  );

  const rows = useMemo<BuildingListRow[]>(
    () =>
      design.buildings.map((building, index) => ({
        building,
        label: getBuildingDisplayName(building, index),
        gfa: getGfaFromStoreys(building.storeys),
        height: getStoreysHeight(building.storeys),
      })),
    [design],
  );

  return (
    <ul className={classes.list}>
      {rows.map((row) => (
        <BuildingRow
          key={row.building.id}
          row={row}
          isHovered={row.building.id === hoveredBuildingId}
          isSelected={
            isBuildingOutlineSelected && row.building.id === selectedBuildingId
          }
          isNoBuildingSelected={!hasVisibleBuildingSelection}
          onSelect={handleSelectBuilding}
          onOpen={handleOpenBuilding}
          onHoverChange={setHoveredBuildingId}
        />
      ))}
    </ul>
  );
};

type BuildingRowProps = {
  row: BuildingListRow;
  /** True while the same building is hovered on the map. */
  isHovered: boolean;
  /** True when the whole building, rather than a storey, is selected. */
  isSelected: boolean;
  /** True when the overview has no active building selection. */
  isNoBuildingSelected: boolean;
  onSelect: (buildingId: Building['id']) => void;
  onOpen: (building: Building) => void;
  onHoverChange: (buildingId?: Building['id']) => void;
};

const BuildingRow: React.FC<BuildingRowProps> = ({
  row,
  isHovered,
  isSelected,
  isNoBuildingSelected,
  onSelect,
  onOpen,
  onHoverChange,
}) => {
  const { classes, cx } = useStyles();
  const [isPointerHovered, setIsPointerHovered] = useState(false);

  const handleClick = useCallback(() => {
    if (isSelected && isPointerHovered) {
      onOpen(row.building);
      return;
    }
    onSelect(row.building.id);
  }, [isPointerHovered, isSelected, onOpen, onSelect, row.building]);

  const handleMouseEnter = useCallback(() => {
    setIsPointerHovered(true);
    onHoverChange(row.building.id);
  }, [onHoverChange, row.building.id]);

  const handleMouseLeave = useCallback(() => {
    setIsPointerHovered(false);
    onHoverChange(undefined);
  }, [onHoverChange]);

  const handleOpen = useCallback(
    () => onOpen(row.building),
    [onOpen, row.building],
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      event.preventDefault();
      handleOpen();
    },
    [handleOpen],
  );

  const heightDisplay = getDisplayValueData(
    { value: row.height, unit: 'm' },
    { outputUnit: 'm' },
  );

  return (
    <li
      className={cx(
        classes.listItem,
        isHovered && classes.listItemHovered,
        isSelected && classes.listItemSelected,
        isNoBuildingSelected && classes.listItemNoSelection,
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classes.labelRow}>
        <span className={classes.nameAction}>
          <Typography className={classes.label}>{row.label}</Typography>
          {isSelected && isPointerHovered && (
            <Tooltip title="Open" placement="top" disableInteractive>
              <Box
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpen();
                }}
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'rgba(0,0,0,0.4)',
                  '&:hover': { color: 'rgba(0,0,0,0.8)' },
                  px: '2px',
                }}
              >
                <Icon category="mui" icon="ArrowForward" size={14} />
              </Box>
            </Tooltip>
          )}
        </span>
        <span className={classes.gfa}>
          <ExpressionTypography value={row.gfa} unit="m²" />
        </span>
      </div>
      <div className={classes.heightRow}>
        <Icon category="quantity" icon="height" size="small" />
        <Typography className={classes.heightLabel}>Height</Typography>
        <span>
          {heightDisplay.value} {heightDisplay.unit}
        </span>
      </div>
    </li>
  );
};

const useStyles = makeStyles<void, 'gfa' | 'label'>()(
  (theme, _params, classes) => ({
    list: {
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      listStyle: 'none',
      // Separates the first building from the section header above it.
      marginTop: 8,
    },
    listItem: {
      // Label on its own line above the bar row.
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      rowGap: theme.spacing(1),
      // Match the storey rows: full-bleed hover surface inside the panel.
      padding: theme.spacing(3, PANEL_CONTENT_DEFAULT_SPACING + 1),
      paddingTop: 8,
      paddingLeft: `calc(${theme.spacing(PANEL_CONTENT_DEFAULT_SPACING + 1)} + 4px)`,
      margin: theme.spacing(0, -PANEL_CONTENT_DEFAULT_SPACING),
      position: 'relative',
      textAlign: 'left',
      cursor: 'pointer',
      userSelect: 'none',
      color: 'rgba(0, 0, 0, 0.5)',
      '&:hover': {
        backgroundColor: 'transparent',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 4px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          borderRadius: 8,
          pointerEvents: 'none',
        },
      },
    },
    /** Same treatment as pointer hover, driven by the map (DEV-2576). */
    listItemHovered: {
      backgroundColor: 'transparent',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '0 4px',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderRadius: 8,
        pointerEvents: 'none',
      },
    },
    listItemSelected: {
      backgroundColor: 'transparent',
      color: 'rgba(0, 0, 0, 1)',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '0 4px',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderRadius: 8,
        pointerEvents: 'none',
      },
      [`& .${classes.label}`]: {
        color: 'rgba(0, 0, 0, 1)',
      },
      [`& .${classes.gfa}`]: {
        color: 'rgba(0, 0, 0, 1)',
      },
    },
    listItemNoSelection: {
      [`& .${classes.label}`]: {
        color: 'rgba(0, 0, 0, 0.8)',
      },
      [`& .${classes.gfa}`]: {
        color: 'rgba(0, 0, 0, 0.8)',
      },
    },
    label: {
      fontFamily: 'Roboto',
      fontSize: 13,
      fontWeight: 500,
      color: 'rgba(0, 0, 0, 0.5)',
      letterSpacing: '0.06em',
      transition: 'color 0.15s ease',
    },
    gfa: {
      // Value and unit share the label's 13px size.
      fontSize: 13,
      color: 'inherit',
      '& span': {
        fontSize: 13,
      },
    },
    heightRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontSize: 13,
      color: 'rgba(0, 0, 0, 0.5)',
    },
    heightLabel: {
      fontSize: 13,
    },
    labelRow: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: theme.spacing(1.5),
    },
    nameAction: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  }),
);
